import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statsAPI, eventAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Calendar,
  Users,
  CheckCircle,
  Mail,
  Plus,
  ArrowRight,
  QrCode,
  BarChart3,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import Layout from '../components/Layout';

export default function Dashboard() {
  const { admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, eventsRes] = await Promise.all([
          statsAPI.get(),
          eventAPI.list(),
        ]);
        setStats(statsRes.data);
        setRecentEvents(eventsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    {
      title: 'Total Events',
      value: stats?.total_events || 0,
      icon: Calendar,
      color: 'text-sky-500',
      bgColor: 'bg-sky-50',
    },
    {
      title: 'Total Attendees',
      value: stats?.total_attendees || 0,
      icon: Users,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50',
    },
    {
      title: 'Checked In',
      value: stats?.total_checked_in || 0,
      icon: CheckCircle,
      color: 'text-violet-500',
      bgColor: 'bg-violet-50',
    },
    {
      title: 'Certificates Sent',
      value: stats?.certificates_sent || 0,
      icon: Mail,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
    },
  ];

  return (
    <Layout activeNav="dashboard">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Welcome back, {admin?.name?.split(' ')[0]}
          </h2>
          <p className="text-muted-foreground">
            Here's an overview of your educational events
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
          {statCards.map((stat) => (
            <Card key={stat.title} className="stat-card border-border">
              <CardContent className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center mb-4`}
                >
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <p className="text-3xl font-bold text-foreground mb-1">
                  {loading ? '-' : stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions & Recent Events */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/events/new" className="block">
                <Button className="w-full justify-start gap-3 h-12" data-testid="create-event-btn">
                  <Plus className="w-5 h-5" />
                  Create New Event
                </Button>
              </Link>
              <Link to="/checkin" className="block">
                <Button variant="outline" className="w-full justify-start gap-3 h-12">
                  <QrCode className="w-5 h-5" />
                  Open Check-in Scanner
                </Button>
              </Link>
              <Link to="/reports" className="block">
                <Button variant="outline" className="w-full justify-start gap-3 h-12">
                  <BarChart3 className="w-5 h-5" />
                  View Reports
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Recent Events */}
          <Card className="border-border md:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Events</CardTitle>
              <Link to="/events">
                <Button variant="ghost" size="sm" className="gap-1">
                  View all
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : recentEvents.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mb-4">No events yet</p>
                  <Link to="/events/new">
                    <Button size="sm" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Create your first event
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3" data-testid="recent-events">
                  {recentEvents.map((event) => (
                    <Link
                      key={event.id}
                      to={`/events/${event.id}`}
                      className="block p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-foreground truncate">{event.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {format(parseISO(event.date), 'MMM d, yyyy')} • {event.venue || event.location}
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
